import React, { useState } from 'react';
import { Divider } from '@mui/material';
import { numberToCurrencyString } from 'further-ui/utils';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import { useEqualisationSection } from '../EqualisationContext';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';
import AvatarWithName from 'components/new/AvatarWithName';

interface CapitalActionCardProps {
  capitalAction: LinkedCapitalAction;
}

const CapitalActionCard: React.FC<CapitalActionCardProps> = ({
  capitalAction,
}) => {
  const { baseCurrency } = useEqualisationSection();

  const [activeTab, setActiveTab] = useState(1);
  const [rows, setRows] = useState(
    capitalAction.newInvestorBreakdown.map((row) => ({
      ...row,
      id: row.commitmentId,
    })),
  );

  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setRows(
        capitalAction.originalInvestorBreakdown.map((row) => ({
          ...row,
          id: row.commitmentId,
          callOverpaidOrUnderpaid: 0,
        })),
      );
    } else {
      setRows(
        capitalAction.newInvestorBreakdown.map((row) => ({
          ...row,
          id: row.commitmentId,
        })),
      );
    }
  };

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investorName',
      label: 'Name',
      render: ({ investorName }) => <AvatarWithName name={investorName} />,
    },
    {
      key: 'investments',
      label: 'Call - Investments',
      render: (row) =>
        numberToCurrencyString(row.investments, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'expenses',
      label: 'Call - Expenses',
      render: (row) =>
        numberToCurrencyString(row.expenses, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'returnOfCapital',
      label: 'Capital refunds',
      render: (row) =>
        numberToCurrencyString(row.returnOfCapital, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'netCallOrDistribution',
      label: 'Net call/(distribution)',
      render: (row) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    ...(activeTab === 1
      ? [
          {
            key: 'callOverpaidOrUnderpaid',
            label: 'Call underpaid/(overpaid)',
            render: (row) =>
              numberToCurrencyString(row.callOverpaidOrUnderpaid, {
                fallback: '-',
                currency: baseCurrency,
                negativeInBrackets: true,
              }),
          },
        ]
      : []),
  ];

  return (
    <>
      <Divider />
      <TabsPanel
        disableFullWidth
        tabs={[
          {
            key: 'newAllocation',
            label: 'New call allocation',
            content: <Table borderless columns={columns} rows={rows} />,
          },
          {
            key: 'originalAllocation',
            label: 'Original call allocation',
            content: <Table borderless columns={columns} rows={rows} />,
          },
        ]}
        onTabChange={(tabKey) =>
          handleTabChange(undefined, tabKey === 'newAllocation' ? 1 : 0)
        }
      />
    </>
  );
};

export default CapitalActionCard;
